import { Button, Stack, Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

function tryFormatJson(text: string): string {
  try { return JSON.stringify(JSON.parse(text), null, 4); }
  catch { return text; }
}

function validateJson(text: string): string | null {
  if (!text.trim()) return null;
  try { JSON.parse(text); return null; }
  catch (e) { return (e as Error).message; }
}

interface ConfigurationInputProps {
  configuration: string,
  onSave: (configuration: string) => void,
  readOnly?: boolean
}

function ConfigurationInput(props: ConfigurationInputProps): React.ReactNode {
  const [configuration, setConfiguration] = useState(() => tryFormatJson(props.configuration || ''));
  const [hasChanged, setHasChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [currentDimensions, setCurrentDimensions] = useState(() => {
    try {
      const saved = sessionStorage.getItem('configTextAreaDimensions');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved dimensions:', e);
    }
    return { width: '100%', height: '400px' };
  });

  useEffect(() => {
    setHasChanged(false);
    setJsonError(null);
    setConfiguration(tryFormatJson(props.configuration || ''));
  }, [props.configuration]);

  const handleResize = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const textarea = e.target as HTMLTextAreaElement;
    const dimensions = {
      width: `${textarea.offsetWidth}px`,
      height: `${textarea.offsetHeight}px`
    };
    setCurrentDimensions(dimensions);
    sessionStorage.setItem('configTextAreaDimensions', JSON.stringify(dimensions));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    if (e.buttons === 1) { // Left mouse button is pressed
      const dimensions = {
        width: `${textarea.offsetWidth}px`,
        height: `${textarea.offsetHeight}px`
      };
      setCurrentDimensions(dimensions);
    }
  };

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleFormat = React.useCallback(() => {
    if (jsonError) return;
    const formatted = tryFormatJson(configuration);
    setConfiguration(formatted);
    setHasChanged(formatted !== props.configuration);
  }, [configuration, jsonError, props.configuration]);

  useEffect(() => {
    if (!isFocused) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 'a' || e.key === 'å')) {
        e.preventDefault();
        handleFormat();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFormat, isFocused]);

  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!hasChanged) return;
    // After a brief moment showing the green, start fading
    const timer = setTimeout(() => {
      setFading(true);
      // After the CSS transition completes, clear the states
      const clearTimer = setTimeout(() => {
        setHasChanged(false);
        setFading(false);
      }, 2000);
      return () => clearTimeout(clearTimer);
    }, 500);
    return () => clearTimeout(timer);
  }, [hasChanged]);

  const borderColor = jsonError ? '#f44336' : (hasChanged && !fading) ? '#81c784' : saving ? '#4caf50' : '#4a4a4a';
  const borderTransition = fading ? 'border-color 2s ease-out' : 'border-color 0.15s ease-in';

  const textAreaStyle: React.CSSProperties = {
    width: currentDimensions.width,
    height: currentDimensions.height,
    padding: '16px',
    fontFamily: 'inherit',
    fontSize: '0.875rem',
    resize: 'both',
    overflow: 'auto',
    backgroundColor: '#2d2d2e',
    color: '#E5E5E5',
    border: `2px solid ${borderColor}`,
    borderRadius: '4px',
    boxSizing: 'border-box',
    transition: borderTransition
  };

  return (
    <Stack
      ref={containerRef}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
        }
      }}
      sx={{ justifyContent: 'space-between', width: '100%' }}
      spacing={3}
    >
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Box
          component="label"
          sx={{
            position: 'absolute',
            top: '-8px',
            left: '10px',
            bgcolor: 'background.paper',
            px: 1,
            fontSize: '0.75rem',
            color: 'text.primary'
          }}
        >
          Configuration
        </Box>
        <textarea
          style={{ ...textAreaStyle, ...(props.readOnly ? { opacity: 0.7 } : {}) }}
          value={configuration}
          readOnly={props.readOnly}
          onChange={props.readOnly ? undefined : (e) => {
            setConfiguration(e.target.value);
            setHasChanged(e.target.value !== props.configuration);
            setJsonError(validateJson(e.target.value));
            setSaving(false);
          }}
          onMouseUp={handleResize}
          onMouseMove={handleMouseMove}
        />
        {jsonError && (
          <Typography sx={{ color: '#f44336', fontSize: '0.75rem', mt: 0.5 }}>
            {jsonError}
          </Typography>
        )}
      </Box>
      {!props.readOnly && <Stack direction="row" spacing={1} sx={{ width: currentDimensions.width }}>
        <Button
          fullWidth
          variant="outlined"
          disabled={!!jsonError}
          onClick={(e) => {
            e.stopPropagation();
            props.onSave(configuration);
            setHasChanged(false);
            setSaving(true);
          }}
          sx={{
            color: '#f75961',
            borderColor: '#f75961',
            '&:hover': {
              borderColor: '#f75961',
              backgroundColor: 'rgba(247, 89, 97, 0.1)'
            }
          }}>
          Save
        </Button>
        <Button
          variant="outlined"
          disabled={!!jsonError}
          onClick={(e) => {
            e.stopPropagation();
            handleFormat();
          }}
          sx={{
            color: '#81c784',
            borderColor: '#81c784',
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: '#81c784',
              backgroundColor: 'rgba(129, 199, 132, 0.1)'
            }
          }}>
          Form<span style={{ textDecoration: 'underline' }}>a</span>t
        </Button>
      </Stack>}
    </Stack>
  );
}

export default ConfigurationInput;